// UPIPaymentModal.jsx
import { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, QrCode, Banknote } from 'lucide-react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import API_BASE_URL from '../../config';

const UPIPaymentModal = ({
  isOpen,
  onClose,
  onPaymentReceived,
  onPayLater,
  amount,
  gymName,
  message,
  transactionId
}) => {
  const [upiId, setUpiId] = useState(null);
  const [fetchedGymName, setFetchedGymName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchUPIDetails();
    }
  }, [isOpen]);

  const fetchUPIDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token;

      if (!userInfo.username || !userInfo.gymcode) {
        throw new Error('User information not found in session');
      }

      const res = await fetch(
        `${API_BASE_URL}/api/gym/getupidetails?gymcode=${encodeURIComponent(userInfo.gymcode)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!res.ok) throw new Error('Failed to fetch UPI details');

      const data = await res.json();
      setUpiId(data.upiId);
      setFetchedGymName(data.gymName);
    } catch (err) {
      console.error('Error fetching UPI details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateUPIString = () => {
    if (!upiId || !fetchedGymName) return '';
    return `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(fetchedGymName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionId || 'Payment')}`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/70 z-[999] flex items-center justify-center p-4">
      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[#2b2b2b]">
          <div className="flex items-center gap-2">
            <QrCode className="text-lime-400" size={20} />
            <h3 className="text-xl font-bold text-white">Payment</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#2b2b2b] rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Message */}
          <div className="text-center">
            <p className="text-gray-300 text-sm mb-2">{message}</p>
            <div className="bg-gradient-to-r from-lime-500/20 to-emerald-500/20 border-2 border-lime-400/50 rounded-xl p-4">
              <p className="text-2xl font-bold text-white mb-1">₹{amount}</p>
              {!loading && !error && upiId && (
                <p className="text-sm text-gray-400">To: {upiId}</p>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center space-y-3 p-4 bg-[#222] rounded-xl">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lime-400"></div>
              <p className="text-gray-400 text-sm">Loading payment details...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center space-y-3 p-4 bg-[#222] rounded-xl">
              <Banknote className="text-yellow-400" size={48} />
              <div className="text-center">
                <p className="text-white font-semibold mb-1">Take Cash Payment</p>
                <p className="text-gray-400 text-xs">UPI not available. Please accept cash for this transaction.</p>
              </div>
            </div>
          )}

          {!loading && !error && (!upiId || !fetchedGymName) && (
            <div className="flex flex-col items-center space-y-3 p-4 bg-[#222] rounded-xl">
              <Banknote className="text-yellow-400" size={48} />
              <div className="text-center">
                <p className="text-white font-semibold mb-1">Take Cash Payment</p>
                <p className="text-gray-400 text-xs">UPI not available. Please accept cash for this transaction.</p>
              </div>
            </div>
          )}

          {!loading && !error && upiId && fetchedGymName && (
            <div className="flex flex-col items-center space-y-3 p-4 bg-[#222] rounded-xl">
              <div className="w-64 h-64 bg-black border-4 border-lime-500/30 rounded-xl flex items-center justify-center">
                <QRCodeCanvas
                  value={generateUPIString()}
                  size={220}
                  bgColor="#000000"
                  fgColor="#ffffff"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400 mb-2">Scan QR to pay</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-6 border-t border-[#2b2b2b] bg-[#111]">
          <button
            onClick={onPayLater}
            className="flex-1 py-3 px-4 bg-[#222] border border-[#2b2b2b] rounded-lg hover:bg-[#2b2b2b] text-gray-300 font-medium transition-all"
          >
            <Clock className="inline mr-2" size={16} />
            Payment Pending
          </button>
          <button
            onClick={onPaymentReceived}
            className="flex-1 py-3 px-4 bg-lime-500 text-black rounded-lg font-medium hover:bg-lime-400 transition-all shadow-lg"
          >
            <CheckCircle className="inline mr-2" size={16} />
            Payment Received
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UPIPaymentModal;
